import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { parseTotpSecretFromUri } from "@/modules/auth/presentation/lib/totp-uri";
import { authClient } from "@/modules/auth/presentation/api/client";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { useEffect, useMemo, useState } from "react";

type Phase = "idle" | "enrolling" | "enabled";

export function TwoFactorSetupCard() {
  const { user, refetch } = useAuthState();
  const [password, setPassword] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [totpURI, setTotpURI] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const twoFactorEnabled =
    (user as { twoFactorEnabled?: boolean } | null)?.twoFactorEnabled === true;

  const totpSecret = useMemo(() => parseTotpSecretFromUri(totpURI), [totpURI]);

  useEffect(() => {
    if (twoFactorEnabled && phase === "idle") {
      setPhase("enabled");
    }
  }, [twoFactorEnabled, phase]);

  useEffect(() => {
    if (phase !== "enrolling" || !totpURI) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(totpURI, {
      width: 240,
      margin: 2,
      color: { dark: "#0f172a", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setQrDataUrl(null);
          toast.error("ສ້າງ QR ບໍ່ໄດ້ — ໃຊ້ລະຫັດລັບຂ້າງລຸ່ມແທນ");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [phase, totpURI]);

  useEffect(() => {
    if (!copiedSecret) return;
    const id = window.setTimeout(() => setCopiedSecret(false), 2000);
    return () => window.clearTimeout(id);
  }, [copiedSecret]);

  const beginEnrollment = (uri: string, codes: string[] = []) => {
    setTotpURI(uri);
    setBackupCodes(codes);
    setPhase("enrolling");
    setVerifyCode("");
  };

  const startEnable = async () => {
    if (password.length < 6) {
      toast.error("ກະລຸນາປ້ອນລະຫັດຜ່ານຢ່າງນ້ອຍ 6 ຕົວ");
      return;
    }

    setLoading(true);
    try {
      const resume = await authClient.twoFactor.getTotpUri({ password });
      if (resume.data?.totpURI) {
        beginEnrollment(resume.data.totpURI);
        toast.message("ສືບຕໍ່ການຕັ້ງຄ່າ 2FA", {
          description: "ສະແກນ QR ຫຼື ປ້ອນລະຫັດລັບ ແລ້ວຢືນຢັນລະຫັດ 6 ຫຼັກ",
        });
        return;
      }

      const { data, error } = await authClient.twoFactor.enable({
        password,
        issuer: "Mineral Admin",
      });
      if (error) {
        toast.error(error.message ?? "ເປີດ 2FA ບໍ່ສຳເລັດ");
        return;
      }
      if (!data?.totpURI) {
        toast.error("ຂໍ້ມູນ 2FA ບໍ່ຄົບ");
        return;
      }
      beginEnrollment(data.totpURI, data.backupCodes ?? []);
    } finally {
      setLoading(false);
    }
  };

  const confirmTotp = async () => {
    setLoading(true);
    try {
      const { error } = await authClient.twoFactor.verifyTotp({
        code: verifyCode.replace(/\D/g, ""),
      });
      if (error) {
        toast.error(error.message ?? "ລະຫັດບໍ່ຖືກຕ້ອງ");
        return;
      }
      toast.success("ເປີດ 2FA ສຳເລັດ");
      setPhase("enabled");
      setTotpURI("");
      setBackupCodes([]);
      setQrDataUrl(null);
      setVerifyCode("");
      setPassword("");
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const disable2fa = async () => {
    setLoading(true);
    try {
      const { error } = await authClient.twoFactor.disable({ password });
      if (error) {
        toast.error(error.message ?? "ປິດ 2FA ບໍ່ສຳເລັດ");
        return;
      }
      toast.success("ປິດ 2FA ແລ້ວ — ສາມາດເປີດໃໝ່ເພື່ອເຫັນ QR");
      setPhase("idle");
      setTotpURI("");
      setBackupCodes([]);
      setQrDataUrl(null);
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const copyTotpSecret = async () => {
    if (!totpSecret) return;
    try {
      await navigator.clipboard.writeText(totpSecret);
      setCopiedSecret(true);
      toast.success("ສຳເນົາລະຫັດລັບແລ້ວ");
    } catch {
      toast.error("ສຳເນົາບໍ່ສຳເລັດ");
    }
  };

  const isEnabled = phase === "enabled" || (twoFactorEnabled && phase !== "enrolling");

  return (
    <Card>
      <CardHeader>
        <CardTitle>ການຢືນຢັນສອງຂັ້ນ (2FA)</CardTitle>
        <CardDescription>
          ໃຊ້ແອບ Google Authenticator ຫຼື Authy — ຈຳເປັນສຳລັບຜູ້ທີ່ອະນຸມັດ wallet
          ຫຼື ເບິ່ງ audit
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {phase === "enrolling" ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
              <p className="font-medium">ຂັ້ນຕອນການຕັ້ງຄ່າ</p>
              <ol className="text-muted-foreground mt-2 list-decimal space-y-1 pl-5 text-sm">
                <li>ຕິດຕັ້ງແອບ Authenticator ບມືຖື</li>
                <li>ສະແກນ QR ຂ້າງລຸ່ມ (ຫຼື ປ້ອນລະຫັດລັບ)</li>
                <li>ປ້ອນລະຫັດ 6 ຫຼັກຈາກແອບ ແລ້ວກົດຢືນຢັນ</li>
              </ol>
            </div>

            <div className="flex flex-col items-center gap-3">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR Code ສຳລັບ 2FA"
                  width={240}
                  height={240}
                  className="rounded-xl border bg-white p-3 shadow-sm"
                />
              ) : (
                <div className="flex size-[240px] items-center justify-center rounded-xl border bg-muted/40">
                  <Loader2 className="text-muted-foreground size-8 animate-spin" />
                </div>
              )}
            </div>

            {totpSecret ? (
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
                  ລະຫັດລັບ (ຖ້າສະແກນ QR ບໍ່ໄດ້)
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="bg-background rounded px-2 py-1 font-mono text-sm break-all">
                    {totpSecret}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void copyTotpSecret()}
                  >
                    <Copy className="mr-1 size-4" />
                    {copiedSecret ? "ສຳເນົາແລ້ວ" : "ສຳເນົາ"}
                  </Button>
                </div>
              </div>
            ) : null}

            {backupCodes.length > 0 ? (
              <div className="rounded-lg border bg-muted/40 p-3 text-xs">
                <p className="mb-2 font-semibold">Backup codes (ເກັບໄວ້ປອດໄພ):</p>
                <ul className="grid grid-cols-2 gap-1 font-mono">
                  {backupCodes.map((code) => (
                    <li key={code}>{code}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="space-y-2">
              <p className="text-sm font-medium">ລະຫັດ 6 ຫຼັກຈາກແອບ Authenticator</p>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                value={verifyCode}
                onChange={(e) =>
                  setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
              <Button
                className="w-full max-w-sm"
                disabled={loading || verifyCode.length !== 6}
                onClick={() => void confirmTotp()}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    ກຳລັງຢືນຢັນ...
                  </>
                ) : (
                  "ຢືນຢັນ 2FA"
                )}
              </Button>
            </div>
          </div>
        ) : isEnabled ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800 dark:text-emerald-300">
                  2FA ເປີດໃຊ້ງານແລ້ວ
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                  ບັນຊີຖືກຜູກກັບແອບ Authenticator ແລ້ວ — ບໍ່ຕ້ອງສະແກນ QR ອີກ
                  ຍົກເວັ້ນຢາກຕັ້ງໃໝ່ (ປິດ 2FA ກ່ອນ)
                </p>
              </div>
            </div>
            <div className="flex max-w-sm flex-col gap-2">
              <Input
                type="password"
                placeholder="ລະຫັດຜ່ານເພື່ອປິດ 2FA"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                variant="destructive"
                disabled={loading || !password}
                onClick={() => void disable2fa()}
              >
                {loading ? "ກຳລັງດຳເນີນການ..." : "ປິດ 2FA ເພື່ອຕັ້ງໃໝ່"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border bg-muted/30 p-4 text-sm">
              <p className="font-medium">ຍັງບໍ່ໄດ້ເປີດ 2FA</p>
              <p className="text-muted-foreground mt-1">
                ປ້ອນລະຫັດຜ່ານ ແລ້ວກົດ &quot;ເປີດ 2FA&quot; — ຈະຂຶ້ນ QR ໃຫ້ສະແກນ
                ໃນຂັ້ນຕອນຖັດໄປ
              </p>
            </div>
            <div className="flex max-w-sm flex-col gap-2">
              <label className="text-sm font-medium" htmlFor="twofa-password">
                ລະຫັດຜ່ານປັດຈຸບັນ
              </label>
              <Input
                id="twofa-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && password.length >= 6 && !loading) {
                    void startEnable();
                  }
                }}
              />
              <Button
                className="w-full"
                disabled={loading || password.length < 6}
                onClick={() => void startEnable()}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    ກຳລັງເຕີມ QR...
                  </>
                ) : (
                  "ເປີດ 2FA ແລະ ສະແດງ QR"
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
